<template>
  <div class="overflow-hidden rounded-lg">
    <div ref="editorElement"></div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import Quill from "quill";
import { sanitizeNewsContent } from "@/utils/newsContent";

const props = defineProps({
  modelValue: { type: String, default: "" },
  placeholder: { type: String, default: "Escribí una descripción..." },
  minHeight: { type: Number, default: 240 },
});

const emit = defineEmits(["update:modelValue"]);
const editorElement = ref(null);
let editor = null;
let updatingFromModel = false;

function removeExternalTextStyles(_node, delta) {
  delta.ops.forEach((op) => {
    if (!op.attributes) return;
    delete op.attributes.font;
    delete op.attributes.size;
    delete op.attributes.color;
    delete op.attributes.background;
    if (!Object.keys(op.attributes).length) delete op.attributes;
  });
  return delta;
}

function setContent(value) {
  if (!editor) return;
  const html = sanitizeNewsContent(value);
  if (editor.root.innerHTML === html) return;
  updatingFromModel = true;
  editor.setText("");
  if (html) editor.clipboard.dangerouslyPasteHTML(html);
  updatingFromModel = false;
}

onMounted(() => {
  editor = new Quill(editorElement.value, {
    theme: "snow",
    placeholder: props.placeholder,
    formats: [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "align",
      "blockquote",
      "link",
    ],
    modules: {
      toolbar: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "link"],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
        matchers: [[window.Node.ELEMENT_NODE, removeExternalTextStyles]],
      },
    },
  });
  editor.root.style.minHeight = `${props.minHeight}px`;
  setContent(props.modelValue);
  editor.on("text-change", () => {
    if (!updatingFromModel) emit("update:modelValue", editor.root.innerHTML);
  });
});

watch(() => props.modelValue, setContent);
</script>

<style scoped>
:deep(.ql-toolbar) {
  border-color: #374151 !important;
  background: #1f2937;
}

:deep(.ql-toolbar .ql-stroke) {
  stroke: #d1d5db !important;
}

:deep(.ql-toolbar .ql-fill) {
  fill: #d1d5db !important;
}

:deep(.ql-toolbar .ql-picker-label) {
  color: #d1d5db !important;
}

:deep(.ql-container) {
  border-color: #374151 !important;
  background: white;
  color: #111;
  font-size: 1rem;
}

:deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: normal;
}
</style>
